/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import Dialog from '@material-ui/core/Dialog';
import {makeStyles} from '@material-ui/core/styles';
import CloseButton from '../components/buttons/CloseButton';
import {PrimaryButton} from '../components/buttons/PrimaryButton';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {
  dlsColor,
  dlsZIndex
} from '../theme/theme';

export interface FilterFavoriteDeleteDialogProps {
  cancelLabel: string;
  className?: string;
  message: string;
  open: boolean;
  primaryLabel: string;
  title: string;
  zIndex?: number;
  onCancel: () => void;
  onDelete: () => void;
}

const useStyles = makeStyles(() => ({
  root : {
    zIndex : ({zIndex}: FilterFavoriteDeleteDialogProps) => (
      `${zIndex ?? dlsZIndex('userNotificationDialogRoot')} !important` as any
    )
  },
  paper : {
    alignItems      : 'center',
    backgroundColor : `${dlsColor('white')} !important`,
    borderRadius    : 12,
    borderTop       : `2px solid ${dlsColor('pastelTeal')}`,
    boxShadow       : '2px 4px 4px rgba(0, 0, 0, 0.25)',
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    height          : 176,
    justifyContent  : 'center',
    margin          : '25px !important',
    maxWidth        : '318px !important',
    padding         : 0,
    width           : 318
  },
  header : {
    alignItems     : 'center',
    alignSelf      : 'stretch',
    boxSizing      : 'border-box',
    display        : 'flex',
    flexGrow       : 0,
    height         : 56,
    justifyContent : 'space-between',
    padding        : '0 16px',
    width          : 318
  },
  title : {
    alignItems : 'center',
    color      : dlsColor('grey1'),
    display    : 'flex',
    fontFamily : 'Inter',
    fontSize   : 16,
    fontWeight : 600,
    height     : 20,
    lineHeight : '20px'
  },
  message : {
    alignItems    : 'center',
    alignSelf     : 'stretch',
    boxSizing     : 'border-box',
    color         : dlsColor('grey1'),
    display       : 'flex',
    flexGrow      : 0,
    fontFamily    : 'Inter',
    fontSize      : 14,
    fontWeight    : 400,
    height        : 56,
    letterSpacing : 0,
    lineHeight    : '20px',
    padding       : '8px 16px',
    width         : 318
  },
  actions : {
    alignItems     : 'center',
    alignSelf      : 'stretch',
    boxSizing      : 'border-box',
    display        : 'flex',
    flexDirection  : 'row',
    flexGrow       : 0,
    gap            : 8,
    height         : 64,
    justifyContent : 'flex-end',
    padding        : '8px 16px',
    width          : 318
  },
  cancelButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    color           : dlsColor('darkestBlue'),
    cursor          : 'pointer',
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 13,
    fontWeight      : 600,
    height          : 32,
    justifyContent  : 'center',
    letterSpacing   : 0.5,
    lineHeight      : '14px',
    padding         : '8px 16px'
  },
  deleteButton : {
    alignItems      : 'center',
    alignSelf       : 'stretch',
    backgroundColor : dlsColor('darkTeal'),
    border          : 0,
    borderRadius    : 8,
    color           : dlsColor('white'),
    cursor          : 'pointer',
    display         : 'flex',
    flexDirection   : 'column',
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    height          : 32,
    justifyContent  : 'center',
    lineHeight      : '16px',
    minHeight       : 32,
    padding         : '0 16px',
    width           : 165
  }
}));

/**
 * Displays the confirmation dialog used before deleting a favorite filter.
 *
 * ```typescript
 * import FilterFavoriteDeleteDialog from '@ion/dls/src/filters/FilterFavoriteDeleteDialog';
 *
 * <FilterFavoriteDeleteDialog open
 *   title='Delete Favorite'
 *   message='This action cannot be undone.'
 *   primaryLabel='Yes, Delete Favorite'
 *   cancelLabel='Cancel'
 *   onCancel={closeDialog}
 *   onDelete={deleteFavorite} />
 * ```
 */
export const FilterFavoriteDeleteDialog = (props: FilterFavoriteDeleteDialogProps) => {
  const {
    cancelLabel,
    className,
    message,
    open,
    primaryLabel,
    title,
    onCancel,
    onDelete
  } = props;
  const classes = useStyles(props);

  return (
    <Dialog open={open}
      classes={{
        root  : `${classes.root} ${className ?? ''}`,
        paper : classes.paper
      }}>
      <div className={classes.header}>
        <div className={classes.title}>{title}</div>
        <CloseButton onClick={onCancel} />
      </div>
      <div className={classes.message}>{message}</div>
      <div className={classes.actions}>
        <TertiaryButton className={classes.cancelButton}
          onClick={onCancel}>
          {cancelLabel}
        </TertiaryButton>
        <PrimaryButton className={classes.deleteButton}
          onClick={onDelete}>
          {primaryLabel}
        </PrimaryButton>
      </div>
    </Dialog>
  );
};

export default memo(FilterFavoriteDeleteDialog);
